import { useMemo } from 'react';
import { motion } from 'framer-motion';
import Markdown from 'react-markdown';
import type { ChatMessage } from '@pullman/shared';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { PropertyCard } from '@/components/cards/PropertyCard';
import { PaymentCalculator } from '@/components/cards/PaymentCalculator';
import { MeetingScheduler } from '@/components/cards/MeetingScheduler';
import type { ApartmentType } from '@pullman/shared';
import propertiesData from '../../../../knowledge-base/data/properties.json';

const propertiesMap = new Map<string, ApartmentType>(
  (propertiesData as ApartmentType[]).map((p) => [p.id, p]),
);

function resolveProperty(data: Record<string, unknown>): ApartmentType | null {
  if ('property' in data && data.property) return data.property as ApartmentType;
  if ('typeId' in data && typeof data.typeId === 'string') return propertiesMap.get(data.typeId) ?? null;
  return null;
}

function cleanContent(text: string): string {
  return text
    .replace(/<lead_score>[\s\S]*?<\/lead_score>/g, '')
    .replace(/<property_card\s+type="[^"]*"\s*\/>/g, '')
    .replace(/<payment_calc\s+type="[^"]*"\s+tier="[^"]*"\s*\/>/g, '')
    .replace(/<meeting_prompt\s*\/>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

interface AIBubbleProps {
  message: ChatMessage;
  onSendMessage?: (text: string) => void;
}

export function AIBubble({ message, onSendMessage }: AIBubbleProps) {
  const reduced = useReducedMotion();
  const time = new Date(message.timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
  const displayContent = useMemo(() => cleanContent(message.content), [message.content]);

  return (
    <motion.div
      className="flex items-start gap-2 px-4 md:px-0"
      initial={reduced ? undefined : { opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, type: 'spring', damping: 20, stiffness: 200 }}
    >
      {/* Avatar */}
      <div className="w-8 h-8 rounded-full bg-brand-navy flex items-center justify-center flex-shrink-0 mt-1">
        <span className="text-xs font-display text-brand-gold">P</span>
      </div>

      <div className="max-w-[80%] md:max-w-[80%] sm:max-w-[90%]">
        {/* Text bubble */}
        {displayContent && (
          <div className="bg-brand-cream rounded-tr-bubble rounded-br-bubble rounded-bl-bubble rounded-tl-[4px] px-5 py-3">
            <div className="text-[15px] leading-relaxed text-brand-charcoal prose prose-sm max-w-none prose-headings:text-brand-navy prose-headings:font-display prose-headings:mt-3 prose-headings:mb-1 prose-p:my-1.5 prose-ul:my-1 prose-li:my-0 prose-strong:text-brand-navy">
              <Markdown>{displayContent}</Markdown>
            </div>
          </div>
        )}

        {/* Rich content */}
        {message.richContent && (
          <div className="mt-3">
            {message.richContent.type === 'property-card' && (() => {
              const property = resolveProperty(message.richContent!.data);
              return property ? (
                <PropertyCard
                  property={property}
                  onAskMore={onSendMessage ? (text) => onSendMessage(text) : undefined}
                />
              ) : null;
            })()}
            {message.richContent.type === 'payment-calculator' && (() => {
              const property = resolveProperty(message.richContent!.data);
              return property ? (
                <PaymentCalculator property={property} />
              ) : null;
            })()}
            {message.richContent.type === 'meeting-scheduler' && (
              <MeetingScheduler />
            )}
          </div>
        )}

        {/* Timestamp */}
        <p className="text-xs text-brand-charcoal/40 mt-1 ml-1">{time}</p>
      </div>
    </motion.div>
  );
}
